import { authHeaders, handleAuthExpired } from "./auth";
import { getJson } from "./http";
import type {
  ScheduledTask,
  TaskTemplate,
  SchedulerDaemonStatus,
  SchedulerAuditEntry,
  SchedulerSkillInfo,
  SchedulerSkillDetail,
  SchedulerGithubRepoInfo,
  SchedulerConnectorInfo,
  SchedulerStreamEvent,
} from "@shared/types";

/**
 * Scheduler REST client (OpenSpec: schedule-tasks.md). Mirrors lib/adminApi.ts:
 * thin fetch wrappers with bearer auth; the server enforces role/quota/ownership.
 * Reads use the shared getJson (lib/http.ts) — same 401 → login handling.
 */

async function send<T>(method: string, url: string, body?: unknown): Promise<T> {
  // Only set the JSON content-type when there IS a body: a bodyless POST (e.g.
  // run-now) with content-type:application/json makes Fastify reject the empty
  // body as FST_ERR_CTP_EMPTY_JSON_BODY (400).
  const hasBody = body !== undefined;
  const res = await fetch(url, {
    method,
    headers: hasBody ? authHeaders({ "content-type": "application/json" }) : authHeaders(),
    ...(hasBody ? { body: JSON.stringify(body) } : {}),
  });
  if (res.status === 401) handleAuthExpired();
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new Error(err.message ?? err.error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

/** Fields the create form submits. */
export interface CreateTaskBody {
  title: string;
  jobType: string;
  prompt?: string;
  description?: string;
  cronExpression?: string;
  recurrencePreset?: string;
  priority?: ScheduledTask["priority"];
  permission?: ScheduledTask["permission"];
  triggerType?: "schedule" | "webhook";
  jobParams?: Record<string, unknown>;
  tags?: string[];
}

/** The public webhook URL for a webhook-triggered task, or null otherwise. */
export function webhookUrl(task: ScheduledTask): string | null {
  if (task.metadata?.triggerType !== "webhook") return null;
  const token = task.metadata?.triggerToken;
  if (typeof token !== "string") return null;
  return `${window.location.origin}/api/scheduler/webhook/${token}`;
}

/** Type label for a connector id's prefix (`git:…` → 代码仓库). */
const CONNECTOR_TYPE_LABELS: Record<string, string> = {
  git: "代码仓库",
  feishu: "飞书",
};

/**
 * The "会用到" connector labels a task/template declares in job_params. Ids are
 * per-connection (`git:{id}` / `feishu:{id}`); `nameMap` (from the connectors
 * list) gives the specific connection name, else we fall back to the type label.
 */
export function connectorLabels(
  jobParams?: Record<string, unknown>,
  nameMap?: Record<string, string>,
): string[] {
  const raw = jobParams?.connectors;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x): x is string => typeof x === "string")
    .map((id) => {
      const named = nameMap?.[id];
      if (named !== undefined) return named;
      const type = id.includes(":") ? id.slice(0, id.indexOf(":")) : id;
      return CONNECTOR_TYPE_LABELS[type] ?? id;
    });
}

/** Display label for a built-in skill id (mirrors the server builtin-skills registry). */
const SKILL_LABELS: Record<string, string> = {
  codeAtlas: "codeAtlas 技能",
};

/**
 * The built-in skill(s) a task/template declares in `job_params.skill` (string or
 * string[]) — rendered as a chip so the card shows which skill the job runs. Unknown
 * ids fall back to their raw id + "技能".
 */
export function skillLabels(jobParams?: Record<string, unknown>): string[] {
  const raw = jobParams?.skill;
  const ids = Array.isArray(raw) ? raw : raw !== undefined ? [raw] : [];
  return ids
    .filter((x): x is string => typeof x === "string" && x.length > 0)
    .map((id) => SKILL_LABELS[id] ?? `${id} 技能`);
}

/** One editable "pull source" field for a job type (so a task is not a black box). */
export interface SourceField {
  key: string;
  label: string;
  kind: "text" | "list";
  placeholder?: string;
  hint?: string;
}

/**
 * Per-jobType pull-source schema. Tasks that consume from a configurable source
 * declare their fields here so the form can render them as editable inputs
 * (prefilled from the template, falling back to the server default when blank).
 */
const SOURCE_SCHEMA: Record<string, SourceField[]> = {
  "ingest.feishu": [
    { key: "spaceIds", label: "飞书 wiki 空间 ID", kind: "list", placeholder: "每行一个；留空用服务端默认", hint: "从哪些飞书 wiki 空间同步" },
    { key: "docTokens", label: "指定文档 token（可选）", kind: "list", placeholder: "每行一个" },
    { key: "folderToken", label: "云文档文件夹 token（可选）", kind: "text", placeholder: "留空则不限定" },
  ],
  "ingest.corpus": [
    { key: "corpusDir", label: "语料目录", kind: "text", placeholder: "corpus", hint: "服务端相对路径，留空用默认 corpus/" },
  ],
};

/** The pull-source fields a job type exposes (empty = no configurable source). */
export function sourceFields(jobType: string): SourceField[] {
  return SOURCE_SCHEMA[jobType] ?? [];
}

/** A one-line "来源" summary for a card/detail, or null when there is none. */
export function sourceSummary(jobType: string, jobParams?: Record<string, unknown>): string | null {
  const fields = sourceFields(jobType);
  if (fields.length === 0) return null;
  const parts: string[] = [];
  for (const f of fields) {
    const v = jobParams?.[f.key];
    if (f.kind === "list" && Array.isArray(v) && v.length > 0) parts.push(`${f.label}: ${v.length} 项`);
    else if (f.kind === "text" && typeof v === "string" && v.length > 0) parts.push(`${f.label}: ${v}`);
  }
  return parts.length > 0 ? parts.join(" · ") : "使用服务端默认来源";
}

/** Map an API error code (or raw message) to a plain-language Chinese message. */
const ERROR_MESSAGES: Record<string, string> = {
  permission_exceeds_handler: "这类任务不支持所选的影响范围。",
  act_requires_admin: "「完全执行」仅管理员可用。",
  unknown_job_type: "未知的任务类型。",
  invalid_cron: "排程表达式无效。",
  invalid_preset: "排程预设无效。",
  task_limit_reached: "你的定时任务数量已达上限。",
  invalid_body: "提交内容有误，请检查名字、指令等必填项。",
  forbidden: "你没有权限执行这个操作。",
  not_found: "任务不存在或已被删除。",
};

/** Friendly message for an error thrown by the api client. */
export function friendlyError(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  if (raw.includes("404")) return "定时任务暂未开放，请联系管理员开启。";
  return ERROR_MESSAGES[raw] ?? raw;
}

/** Plain-language risk badge derived from the (hidden) permission tier. */
export function riskBadge(permission: ScheduledTask["permission"]): { label: string; tone: string } {
  switch (permission) {
    case "observe":
      // 只读 is the safe default — neutral, not colored (color scarcity).
      return { label: "只读", tone: "neutral" };
    case "act":
      return { label: "完全执行", tone: "err" };
    default:
      return { label: "会改动内容", tone: "warn" };
  }
}

export const schedulerApi = {
  templates: (): Promise<{ templates: TaskTemplate[] }> =>
    getJson(`/api/scheduler/templates`),
  skills: (): Promise<{ skills: SchedulerSkillInfo[] }> =>
    getJson(`/api/scheduler/skills`),
  skillDetail: (tenant: string, name: string): Promise<{ skill: SchedulerSkillDetail }> =>
    getJson(`/api/scheduler/skills/${encodeURIComponent(tenant)}/${encodeURIComponent(name)}`),
  connectors: (): Promise<{ connectors: SchedulerConnectorInfo[] }> =>
    getJson(`/api/scheduler/connectors`),
  uploadSkill: async (file: File, tenant?: string): Promise<{ name: string; tenant: string }> => {
    const fd = new FormData();
    fd.append("file", file);
    const q = tenant !== undefined && tenant.length > 0 ? `?tenant=${encodeURIComponent(tenant)}` : "";
    const res = await fetch(`/api/scheduler/skills${q}`, { method: "POST", headers: authHeaders(), body: fd });
    if (!res.ok) {
      const e = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      throw new Error(e.message ?? e.error ?? `HTTP ${res.status}`);
    }
    return (await res.json()) as { name: string; tenant: string };
  },
  validateGithubSkill: (url: string): Promise<{ repo: SchedulerGithubRepoInfo }> =>
    send("POST", `/api/scheduler/skills/import-github/validate`, { url }),
  importGithubSkill: (url: string, tenant?: string): Promise<{ name: string; tenant: string }> =>
    send("POST", `/api/scheduler/skills/import-github`, { url, ...(tenant !== undefined && tenant.length > 0 ? { tenant } : {}) }),
  uninstallSkill: (tenant: string, name: string): Promise<{ ok: boolean }> =>
    send("DELETE", `/api/scheduler/skills/${encodeURIComponent(tenant)}/${encodeURIComponent(name)}`),
  /**
   * Subscribe to the live scheduler event stream (SSE over fetch — EventSource
   * can't send the auth header). Calls onEvent per event until `signal` aborts.
   */
  subscribeEvents(onEvent: (e: SchedulerStreamEvent) => void, signal: AbortSignal): void {
    void (async () => {
      try {
        const res = await fetch("/api/scheduler/events", {
          headers: authHeaders({ Accept: "text/event-stream" }),
          signal,
        });
        // A 401 here means the token expired — surface login (which unmounts this
        // page and aborts the stream) instead of reading the error body as events.
        if (res.status === 401) { handleAuthExpired(); return; }
        if (res.body === null) return;
        const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
        let buf = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += value;
          const frames = buf.split("\n\n");
          buf = frames.pop() ?? "";
          for (const frame of frames) {
            const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
            if (dataLine === undefined) continue;
            try {
              onEvent(JSON.parse(dataLine.slice(5).trim()) as SchedulerStreamEvent);
            } catch {
              /* skip a malformed frame */
            }
          }
        }
      } catch {
        /* aborted or network drop — the page still works via manual reload */
      }
    })();
  },
  list: (params: { status?: string; owner?: string } = {}): Promise<{ tasks: ScheduledTask[] }> => {
    const q = new URLSearchParams();
    if (params.status !== undefined) q.set("status", params.status);
    if (params.owner !== undefined) q.set("owner", params.owner);
    const qs = q.toString();
    return getJson(`/api/scheduler/tasks${qs.length > 0 ? `?${qs}` : ""}`);
  },
  get: (id: string): Promise<{ task: ScheduledTask }> =>
    getJson(`/api/scheduler/tasks/${id}`),
  create: (body: CreateTaskBody): Promise<{ task: ScheduledTask }> =>
    send("POST", `/api/scheduler/tasks`, body),
  update: (id: string, body: Partial<CreateTaskBody> & { status?: ScheduledTask["status"] }): Promise<{ task: ScheduledTask }> =>
    send("PUT", `/api/scheduler/tasks/${id}`, body),
  remove: (id: string): Promise<{ ok: boolean }> =>
    send("DELETE", `/api/scheduler/tasks/${id}`),
  runNow: (id: string): Promise<{ ok: boolean }> =>
    send("POST", `/api/scheduler/tasks/${id}/run-now`),
  executions: (id: string): Promise<{ executions: SchedulerAuditEntry[] }> =>
    getJson(`/api/scheduler/tasks/${id}/executions`),
  daemonStatus: (): Promise<{ daemon: SchedulerDaemonStatus }> =>
    getJson(`/api/scheduler/daemon/status`),
};

/** The 12 recurrence presets + labels (UI mirror of server/scheduler/recurrence). */
export const PRESET_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "不重复（一次性）" },
  { value: "every_minute", label: "每分钟" },
  { value: "every_5_minutes", label: "每 5 分钟" },
  { value: "every_15_minutes", label: "每 15 分钟" },
  { value: "every_30_minutes", label: "每 30 分钟" },
  { value: "hourly", label: "每小时" },
  { value: "daily", label: "每天 9:00" },
  { value: "weekdays", label: "工作日 9:00" },
  { value: "weekends", label: "周末 10:00" },
  { value: "weekly", label: "每周一 9:00" },
  { value: "monthly", label: "每月 1 号 9:00" },
];

/** Label for a task's schedule (preset label, else raw cron, else 一次性). */
export function scheduleLabel(task: ScheduledTask): string {
  if (task.metadata?.triggerType === "webhook") return "由外部事件触发";
  if (task.recurrencePreset !== undefined && task.recurrencePreset.length > 0) {
    return PRESET_OPTIONS.find((p) => p.value === task.recurrencePreset)?.label ?? task.recurrencePreset;
  }
  if (task.cronExpression !== undefined && task.cronExpression.length > 0) return task.cronExpression;
  return "一次性";
}
